import { type FC, type ReactNode, useId } from "react";
import { Tooltip as ReactTooltip } from "react-tooltip";
import { cn } from "../../lib/utils";
import { Sparkline, type SparklineTone } from "../../primitives";
import { MetricCard, type TrendDirection } from "./MetricCard";

const EMPTY_SERIES: number[] = [];

const PILL_TONE: Record<SparklineTone, string> = {
	accent: "bg-surface-accent text-brand-accent",
	confirm: "bg-surface-confirm text-confirm",
	danger: "bg-surface-danger text-danger",
	neutral: "bg-surface-neutral text-tertiary",
};

export type TrendDeltaFormat = "percent" | "count";

export type MetricTrend = { display: string; direction: TrendDirection };

type DerivedTrend = MetricTrend;

/** Returns `null` when there's no trend to show: missing inputs, no movement, or a non-positive prior in `percent` mode (divide-by-zero). */
export const deriveTrend = (
	current: number | undefined,
	prior: number | undefined,
	format: TrendDeltaFormat,
): DerivedTrend | null => {
	if (current == null || prior == null || current === prior) return null;
	const direction: TrendDirection = current > prior ? "up" : "down";
	if (format === "percent") {
		if (prior <= 0) return null;
		const pct = Math.abs((current - prior) / prior) * 100;
		const rounded = pct > 1000 ? Math.round(pct) : Number(pct.toFixed(1));
		return { display: `${rounded}%`, direction };
	}
	return { display: `${Math.abs(current - prior)}`, direction };
};

export type TrendMetricCardProps = {
	/** Root `data-testid`, and the prefix for the `-trend-pill`/`-sparkline` test ids. */
	testId?: string;
	label: string;
	/** Formatted metric value, or a node (e.g. a `Spinner`) while loading. */
	value: ReactNode;
	/** When set, hovering the value reveals this text in a popover (e.g. the full, unabbreviated dollar amount). */
	valueTooltip?: string;
	/** Sparkline series, oldest → newest. Fewer than two points hides the sparkline. */
	series?: number[];
	/** Current-window total; paired with `prior` to derive the trend pill. */
	current?: number;
	/** Prior-window total. For `percent` format the pill hides when this is <= 0. */
	prior?: number;
	deltaFormat?: TrendDeltaFormat;
	/** Overrides derived percent/count changes, for metrics expressed in units such as rate points. */
	trend?: MetricTrend | null;
	/** Shared color for the trend pill and sparkline. */
	tone?: SparklineTone;
	caption?: string;
	footer?: ReactNode;
	action?: ReactNode;
	className?: string;
	trackingId?: string;
};

export const TrendMetricCard: FC<TrendMetricCardProps> = ({
	testId,
	label,
	value,
	valueTooltip,
	series = EMPTY_SERIES,
	current,
	prior,
	deltaFormat = "percent",
	trend: trendOverride,
	tone = "accent",
	caption,
	footer,
	action,
	className,
	trackingId,
}) => {
	const trend =
		trendOverride !== undefined ? trendOverride : deriveTrend(current, prior, deltaFormat);
	const valueTooltipId = useId();
	const showValueTooltip = valueTooltip != null && valueTooltip !== "";

	return (
		<MetricCard
			data-testid={testId}
			className={cn("gap-1.5 lg:gap-3 shadow-md", className)}
			trackingId={trackingId}
			action={action}
		>
			<MetricCard.Label>{label}</MetricCard.Label>
			<MetricCard.ValueRow className="items-start justify-between">
				<MetricCard.Value size="lg" className="text-xl lg:text-3xl leading-none tabular-nums">
					{showValueTooltip ? (
						<span
							data-tooltip-id={valueTooltipId}
							data-tooltip-content={valueTooltip}
							data-testid={testId ? `${testId}-value` : undefined}
							className="cursor-help"
						>
							{value}
						</span>
					) : (
						value
					)}
				</MetricCard.Value>
				{trend && (
					<div className="flex flex-col items-center gap-1">
						<MetricCard.Trend
							direction={trend.direction}
							data-testid={testId ? `${testId}-trend-pill` : undefined}
							className={cn("gap-0.5 rounded-full px-2 py-0.5 leading-none", PILL_TONE[tone])}
						>
							{trend.display}
						</MetricCard.Trend>
						{caption && (
							<MetricCard.Caption className="text-[8px] lg:text-[10px] font-normal">
								{caption}
							</MetricCard.Caption>
						)}
					</div>
				)}
			</MetricCard.ValueRow>
			{series.length >= 2 && (
				<Sparkline
					points={series}
					tone={tone}
					data-testid={testId ? `${testId}-sparkline` : undefined}
					className="h-5 lg:h-10 w-full"
				/>
			)}
			{footer != null && footer}
			{showValueTooltip && <ReactTooltip id={valueTooltipId} style={{ zIndex: 9999 }} />}
		</MetricCard>
	);
};
